import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import spacetime, { TimeUnit } from 'spacetime';
import { Spacetime } from 'spacetime/types/types';
import { ValidationErrors } from 'final-form';

import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { IReportItem } from 'APIPackage/MainModule/interfaces/IReportItem';
import { monthOptions } from 'CorePackage/MainModule/constants/monthOptions';
import { currentYear } from 'CorePackage/MainModule/constants/currentYear';
import { yearOptionsWithNext } from 'CorePackage/MainModule/constants/yearOptions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getFederalLaw248SelectOptions,
  getFederalLaw294SelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import {
  getAccountKnoOrganizationsSelectOptions,
  getAccountProsecutorOrganizationsSelectOptions,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IFederalLawSelectOption } from 'CorePackage/CatalogsModule/interfaces/IFederalLawSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { UploadFileTypesEnum } from 'CorePackage/MainModule/enums/UploadFileTypesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import styles from './ReportSettings.module.scss';
import { IReportSettingsForm } from '../interfaces/IReportSettingsForm';
import { reportsDownloadExtendedReport } from '../actions/reportsActions';
import { DatePickerField } from '../../../UIPackage/MainModule/components/DatePickerField';
import { Dropdown } from '../../../UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from '../../../UIPackage/MainModule/interfaces/IDropdownItem';
import { DropdownItem } from '../../../UIPackage/MainModule/components/DropdownItem';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { makeNoSoonerThenValidator } from '../../../CorePackage/MainModule/validators/isNoSoonerThan';

const FZ248 = '248 ФЗ';
const FZ294 = '294 ФЗ';

const currentMonth = (new Date().getMonth() + 1).toString();

const types: ISelectOption<number>[] = [
  {
    id: 0,
    label: ExportFormatsEnum.ODS,
    value: ExportFormatsEnum.ODS,
  },
  {
    id: 1,
    label: ExportFormatsEnum.XLSX,
    value: ExportFormatsEnum.XLSX,
  },
];

const formatSpacetimeToForm = (date: Spacetime): string =>
  formatDate({ date: date.toNativeDate(), dateFormat: DATE_FORMAT_LONG_FNS });

export interface IReportSettingsProps {
  activeItem?: IReportItem;
  federalLaws: IFederalLawSelectOption[];
  isErknm: boolean;
  isSupervisor: boolean;
  knoSelectOptions: IUserOrganisationSelectOption[];
  prosecutorSelectOptions: IUserOrganisationSelectOption[];
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
}

const ReportSettingsWithoutStore = ({
  activeItem,
  federalLaws,
  isErknm,
  isSupervisor,
  knoSelectOptions,
  prosecutorSelectOptions,
  erknmOrganisationsSelectOptions,
}: IReportSettingsProps) => {
  const [isActiveIdChanged, setIsActiveIdChanged] = useState(false);
  const [isControlCheckboxActive, setIsControlCheckboxActive] = useState(true);
  const [defaultPlanType, setDefaultPlanType] = useState(FZ294);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    if (isErknm) {
      setDefaultPlanType(FZ248);
    } else {
      setDefaultPlanType(FZ294);
    }
  }, [isErknm, federalLaws]);

  useEffect(() => {
    setIsActiveIdChanged(true);
    setTimeout(() => setIsActiveIdChanged(false), 10);
  }, [activeItem?.type, isErknm]);

  const [initialKnoOrg] = knoSelectOptions.filter(org => org.byDefault);

  const initialErvkKnoOrg =
    initialKnoOrg && erknmOrganisationsSelectOptions
      ? erknmOrganisationsSelectOptions.find(org => String(org.id) === initialKnoOrg.id)
      : undefined;

  const [initialProcOrg] = prosecutorSelectOptions.filter(org => org.byDefault);
  const initialValues: IReportSettingsForm = {
    year: { id: currentYear, value: currentYear, label: currentYear },
    month: monthOptions.find(month => month.id === currentMonth),
    control: initialErvkKnoOrg ?? initialKnoOrg,
    controlCheckbox: false,
    prosecutor: initialProcOrg,
    prosecutorCheckbox: false,
    number: undefined,
    type: undefined,
    federalLawName: federalLaws.find(item => item.value === defaultPlanType),
  };
  let hasPeriod: boolean;
  let hasYear: boolean;
  let hasMonth: boolean;
  let hasFederalLawName: boolean;
  let hasOrganizations: boolean;
  let hasOrganizationChildrenIncluded: boolean;
  let hasSpecialOrganizationsIncluded: boolean;
  let hasProsecutors: boolean;
  let hasPlanNumber: boolean;

  if (activeItem?.type) {
    hasPeriod =
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_RF_REGION ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_RF_REGION;
    hasYear =
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.APPROVED_KNM_PLAN &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_RF_REGION &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_RF_REGION;
    hasMonth =
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.PLANNED_FOR_MONTH ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.COMPLETED_WITHIN_MONTH ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.KNM_FROM_SMEV_START_DATE ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.KNM_FROM_SMEV_RECEIVED_DATE ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_KNM_PLANNED_BY_MONTH ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_MONTH ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_MONTH ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_PM_PLANNED_BY_MONTH;
    hasFederalLawName =
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.RESULT_KNM_BY_PROSECUTOR_AND_KNO ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.VIOLATIONS_KNM_BY_PROSECUTOR_AND_KNO;
    hasOrganizations = UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.APPROVED_KNM_PLAN;
    hasOrganizationChildrenIncluded =
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_RF_REGION &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_RF_REGION;
    hasSpecialOrganizationsIncluded =
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_RF_REGION ||
      UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_RF_REGION;
    hasProsecutors =
      hasOrganizations &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_BY_KNO &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_BY_SUPERVISION_TYPE &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_PLANNED_BY_MONTH &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_MONTH &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_DECISION_APPEALED &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_KNM_CARRIED_OUT_BY_RF_REGION &&
      UploadFileTypesEnum[activeItem?.type] !== UploadFileTypesEnum.ERKNM_PM_CARRIED_OUT_BY_RF_REGION;
    hasPlanNumber = UploadFileTypesEnum[activeItem?.type] === UploadFileTypesEnum.APPROVED_KNM_PLAN;
  }

  const onControlChange = useCallback((value?: ISelectOption<string | number>) => {
    setIsControlCheckboxActive(!!value);
  }, []);

  function handleFormSubmit(values: IReportSettingsForm) {
    dispatch(
      reportsDownloadExtendedReport(
        { ...values, controlCheckbox: hasOrganizationChildrenIncluded ? values.controlCheckbox : true },
        activeItem,
        {
          hasFederalLawName,
          hasMonth,
          hasOrganizations,
          hasSpecialOrganizationsIncluded,
          hasPlanNumber,
          hasProsecutors,
          hasYear,
          hasPeriod,
        },
      ),
    );
  }

  const formValidator = (values: IReportSettingsForm): ValidationErrors => {
    return {
      startDate: hasPeriod ? makeRequired('Поле не может быть пустым')(values.startDate) : undefined,
      endDate: hasPeriod
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeNoSoonerThenValidator('Дата окончания не должна быть раньше даты начала', () => values.startDate),
          )(values.endDate)
        : undefined,
    };
  };

  function renderForm({ handleSubmit, form }: FormRenderProps<IReportSettingsForm>) {
    if (isActiveIdChanged) {
      form.setConfig('keepDirtyOnReinitialize', false);
      form.reset();
      form.setConfig('keepDirtyOnReinitialize', true);
    }

    const handleCurrentPeriod = (unit: TimeUnit) => {
      const date = spacetime.now();

      form.change('startDate', formatSpacetimeToForm(date.startOf(unit)));
      form.change('endDate', formatSpacetimeToForm(date.endOf(unit)));
    };

    const handlePreviousPeriod = (unit: TimeUnit) => {
      const date = spacetime.now().last(unit);

      form.change('startDate', formatSpacetimeToForm(date.startOf(unit)));
      form.change('endDate', formatSpacetimeToForm(date.endOf(unit)));
    };

    const periodDropdownItems: IDropdownItem[] = [
      {
        id: 'period-item-current-month',
        content: <DropdownItem onClick={() => handleCurrentPeriod('month')}>Текущий месяц</DropdownItem>,
      },
      {
        id: 'period-item-previous-month',
        content: <DropdownItem onClick={() => handlePreviousPeriod('month')}>Предыдущий месяц</DropdownItem>,
      },
      {
        id: 'divider-1',
        isDivider: true,
      },
      {
        id: 'period-item-current-quarter',
        content: <DropdownItem onClick={() => handleCurrentPeriod('quarter')}>Текущий квартал</DropdownItem>,
      },
      {
        id: 'period-item-previous-quarter',
        content: <DropdownItem onClick={() => handlePreviousPeriod('quarter')}>Предыдущий квартал</DropdownItem>,
      },
      {
        id: 'divider-2',
        isDivider: true,
      },
      {
        id: 'period-item-current-year',
        content: <DropdownItem onClick={() => handleCurrentPeriod('year')}>Текущий год</DropdownItem>,
      },
      {
        id: 'period-item-previous-year',
        content: <DropdownItem onClick={() => handlePreviousPeriod('year')}>Предыдущий год</DropdownItem>,
      },
    ];

    return (
      <form onSubmit={handleSubmit}>
        {hasPeriod && (
          <>
            <Row margin="none">
              <FieldBlock
                label="Период"
                color="black"
                button={
                  <div className={styles.PeriodDropdown}>
                    <Dropdown items={periodDropdownItems}>
                      <Button text="Выбрать" variant="link" color="primary" type="button" className="shared-btn-add" />
                    </Dropdown>
                  </div>
                }
              ></FieldBlock>
            </Row>
            <Row>
              <FieldBlock size="half">
                <DatePickerField name="startDate" placeholder="Дата начала" />
              </FieldBlock>
              <FieldBlock size="half">
                <DatePickerField name="endDate" placeholder="Дата окончания" />
              </FieldBlock>
            </Row>
          </>
        )}
        {hasYear && (
          <Row>
            <FieldBlock label="Год" color="black">
              <SelectInputField
                name="year"
                id="year"
                size="medium"
                options={yearOptionsWithNext}
                validate={composeValidators(makeRequired('Поле не может быть пустым'))}
                validateOnSubmit
              />
            </FieldBlock>
          </Row>
        )}
        {hasMonth && (
          <Row>
            <FieldBlock label="Месяц" color="black">
              <SelectInputField
                name="month"
                size="medium"
                options={monthOptions}
                validate={composeValidators(makeRequired('Поле не может быть пустым'))}
                validateOnSubmit
              />
            </FieldBlock>
          </Row>
        )}
        {hasFederalLawName && (
          <Row>
            <FieldBlock label="Тип плана КНМ" color="black">
              <SelectInputField
                name="federalLawName"
                size="medium"
                options={federalLaws}
                validate={composeValidators(makeRequired('Поле не может быть пустым'))}
                disabled={isErknm}
                validateOnSubmit
              />
            </FieldBlock>
          </Row>
        )}
        {hasOrganizations && (
          <>
            <Row>
              <FieldBlock label="Орган контроля" color="black">
                <SelectInputField
                  name="control"
                  id="control"
                  size="medium"
                  options={erknmOrganisationsSelectOptions ?? knoSelectOptions}
                  isSearchable
                  validateOnSubmit
                  onChange={onControlChange}
                  validate={isErknm && isSupervisor ? makeRequired('Поле не может быть пустым') : undefined}
                />
                {hasOrganizationChildrenIncluded && (
                  <CheckboxField
                    className={styles.FormGroupCheckbox}
                    id="controlCheckbox"
                    name="controlCheckbox"
                    type="checkbox"
                    label="Включая подчиненные организации"
                    theme="system"
                    size="medium"
                    color="black"
                    disabled={!isControlCheckboxActive}
                  />
                )}
                {hasSpecialOrganizationsIncluded && (
                  <CheckboxField
                    className={styles.FormGroupCheckbox}
                    id="specialControlCheckbox"
                    name="specialControlCheckbox"
                    type="checkbox"
                    label="Включая специализированные и военные прокуратуры"
                    theme="system"
                    size="medium"
                    color="black"
                  />
                )}
              </FieldBlock>
            </Row>
            {hasProsecutors && (
              <>
                <Row>
                  <FieldBlock label="Орган прокуратуры" color="black">
                    <SelectInputField
                      name="prosecutor"
                      size="medium"
                      options={prosecutorSelectOptions}
                      validate={composeValidators(makeRequired('Поле не может быть пустым'))}
                      isSearchable
                      validateOnSubmit
                    />
                    <CheckboxField
                      className={styles.FormGroupCheckbox}
                      id="prosecutorCheckbox"
                      name="prosecutorCheckbox"
                      type="checkbox"
                      label="Включая подчиненные организации"
                      theme="system"
                      size="medium"
                      color="black"
                    />
                  </FieldBlock>
                </Row>
              </>
            )}
          </>
        )}
        {hasPlanNumber && (
          <Row>
            <FieldBlock label="Номер плана" color="black">
              <TextInputField
                name="number"
                validateOnSubmit
                validate={composeValidators(makeRequired('Поле не может быть пустым'))}
              />
            </FieldBlock>
          </Row>
        )}
        <Row>
          <FieldBlock label="Тип документа" color="black">
            <SelectInputField
              name="type"
              id="type"
              size="medium"
              options={types}
              validate={composeValidators(makeRequired('Поле не может быть пустым'))}
              validateOnSubmit
            />
          </FieldBlock>
        </Row>
        <Button
          className={styles.SubmitButton}
          color="primary"
          size="medium"
          id="submitButton"
          text="Сформировать"
          type="submit"
        />
      </form>
    );
  }

  return (
    <div className={styles.Block}>
      <Row>
        <h2 className="shared-title">Настройки отчета</h2>
      </Row>
      {!activeItem?.type ? (
        <p className={styles.EmptyNotification}>Отчет не выбран</p>
      ) : (
        <Form<IReportSettingsForm>
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          render={renderForm}
          validate={formValidator}
          subscription={{}}
          keepDirtyOnReinitialize
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  federalLaws:
    getLawInterface(state) === LawInterfacesEnum.FZ_248
      ? getFederalLaw248SelectOptions(state)
      : getFederalLaw294SelectOptions(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isSupervisor: getUserRoleIsSupervisor(state),
  knoSelectOptions: getAccountKnoOrganizationsSelectOptions(state),
  prosecutorSelectOptions: getAccountProsecutorOrganizationsSelectOptions(state),
  erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
});

export const ReportSettings = connect(mapStateToProps)(ReportSettingsWithoutStore);
